import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Shell from './components/Shell';
import { SoundProvider } from './hooks/useAlertSound';
import { LiveProvider, useLive } from './hooks/useLive';
import { isAdmin } from './lib/status';
import Admin from './pages/Admin';
import Coverage from './pages/Coverage';
import TenantDetail from './pages/TenantDetail';
import AlertDetail from './pages/AlertDetail';
import Alerts from './pages/Alerts';
import Assets from './pages/Assets';
import Dashboard from './pages/Dashboard';
import Health from './pages/Health';
import Login from './pages/Login';
import PublicAlert from './pages/PublicAlert';
import PublicMap from './pages/PublicMap';
import Closures from './pages/Closures';
import Rules from './pages/Rules';
import Settings from './pages/Settings';
import Wallboard from './pages/Wallboard';

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, meLoaded } = useLive();
  const location = useLocation();
  if (!meLoaded) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9fb0c8' }} role="status">
        Loading WeatherWatch…
      </div>
    );
  }
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

/*
 * Allow-list, not `role !== 'viewer'`.
 *
 * A deny-list means every role added later is an administrator by default, silently and
 * at compile time. `isAdmin` names the two roles that may administer a county, so a new
 * role has to be added to it deliberately.
 */
function RequireAdmin({ children }: { children: ReactNode }) {
  const { me } = useLive();
  if (!isAdmin(me?.user.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Operator-only. RequireAdmin admits tenant_admin, which must not reach tenant management. */
function RequireSuper({ children }: { children: ReactNode }) {
  const { me } = useLive();
  if (!me || me.user.role !== 'super_admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <LiveProvider>
      {/* Inside LiveProvider (it needs the dashboard) and outside Routes (the wallboard
          renders without the Shell, and is the screen most likely to be the only thing
          anyone is watching). */}
      <SoundProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Public: the target of the short link in every notification SMS. Deliberately
            outside RequireAuth — a recipient in the field has no account. */}
        <Route path="/a/:code" element={<PublicAlert />} />
        {/* Public: the county's map link, printed on a county website. Same reasoning —
            no session, so no Shell and no RequireAuth. */}
        <Route path="/m/:code" element={<PublicMap />} />
        <Route
          path="/wallboard"
          element={
            <RequireAuth>
              <Wallboard />
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="alerts" element={<Alerts />} />
          <Route path="alerts/:id" element={<AlertDetail />} />
          <Route path="assets" element={<Assets />} />
          {/* Not RequireAdmin: a dispatcher must be able to reach this, and the page
              gates its own write controls on canEditClosures. */}
          <Route path="closures" element={<Closures />} />
          <Route path="rules" element={<Rules />} />
          <Route path="health" element={<Health />} />
          {/* Not admin-gated: the mute and volume on this page belong to whoever is
              sitting at the machine, and a viewer needs them as much as anyone. The
              county-wide tone choices inside are gated on the role instead. */}
          <Route path="settings" element={<Settings />} />
          <Route
            path="coverage"
            element={
              <RequireAdmin>
                <Coverage />
              </RequireAdmin>
            }
          />
          <Route
            path="admin"
            element={
              <RequireAdmin>
                <Admin />
              </RequireAdmin>
            }
          />
          <Route
            path="admin/tenants/:id"
            element={
              <RequireSuper>
                <TenantDetail />
              </RequireSuper>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </SoundProvider>
    </LiveProvider>
  );
}
